import type { ComponentType, ReactNode } from 'react';
import { usePermission } from '@backstage/plugin-permission-react';
import type { BasicPermission } from '@backstage/plugin-permission-common';

/**
 * A sidebar entry that is simply absent when the person cannot use it.
 *
 * Hidden rather than disabled: a greyed-out row still advertises a feature
 * someone was not given, and invites them to go and ask for it. The route
 * behind it is guarded separately — this is navigation, not enforcement.
 *
 * `usePermission` is stale-while-revalidate, so an item does not flicker in
 * and out on every render while the decision is refreshed.
 */
export const PermissionSidebarItem = (props: {
  permission: BasicPermission;
  /** Rendered only when allowed. Kept as a render prop so hooks stay at the top. */
  children: ReactNode;
}) => {
  const { allowed } = usePermission({ permission: props.permission });
  return allowed ? <>{props.children}</> : null;
};

/**
 * Wraps a sidebar item component, for the common case of one item per
 * permission.
 */
export function withPermission<P extends object>(
  Component: ComponentType<P>,
  permission: BasicPermission,
) {
  return (props: P) => (
    <PermissionSidebarItem permission={permission}>
      <Component {...props} />
    </PermissionSidebarItem>
  );
}
